import type { AuthUser } from '@stayon/auth-core';
import { useAuth } from '@/shared/auth';

export const OBJECT_SCOPE_STORAGE = 'stayon.object-scope';
export const CLEANING_SCOPE_STORAGE = 'stayon.cleaning-scope';

/**
 * Eine gespeicherte Auswahl gehört einem Benutzer in einem Workspace, im Support also dem
 * Ziel-Workspace. So liest ein Workspace nie die Objekt-Id eines anderen, auch nicht in einem
 * zweiten Tab, der noch im vorigen Workspace arbeitet.
 */
export function scopeStorageKey(base: string, user: AuthUser | null): string | null {
  return user ? `${base}:${user.tenantId}:${user.userId}` : null;
}

export function useScopeStorageKey(base: string): string | null {
  return scopeStorageKey(base, useAuth().user);
}

// Ohne Speicher (privates Fenster, gesperrte Website-Daten) gilt einfach keine Auswahl. Ein Fehler
// hier darf weder eine Seite noch einen Identitätswechsel aufhalten.
function guarded<T>(work: () => T, fallback: T): T {
  try {
    return work();
  } catch {
    return fallback;
  }
}

export function readScope(key: string | null): string | null {
  return key ? guarded(() => localStorage.getItem(key), null) : null;
}

export function writeScope(key: string | null, id: string | null): void {
  if (!key) return;
  guarded(() => (id ? localStorage.setItem(key, id) : localStorage.removeItem(key)), undefined);
}

/** Leert jede gespeicherte Objekt- und Reinigungsauswahl, auch die ohne Workspace-Schlüssel. */
export function clearScopeSelections(): void {
  for (const key of guarded(() => Object.keys(localStorage), [])) {
    if (key.startsWith(OBJECT_SCOPE_STORAGE) || key.startsWith(CLEANING_SCOPE_STORAGE)) {
      guarded(() => localStorage.removeItem(key), undefined);
    }
  }
}
